import { MouseEventHandler, useEffect, useMemo } from 'react';

import { connect } from 'react-redux';

import { useHistory } from 'react-router-dom';

import O2UserPopper from '@o2/common-business/UserPopper';
import { Calendar, Home, Settings } from '@o2/icons';

import Button from '@o2/ui/Button';
import Grid from '@o2/ui/Grid';

import { PopoverState } from './NewAppBarInternal';
import { CustomerSegment } from 'generated/bffCustomer/models';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ROUTES } from 'helpers/routes/routes';

import { AppState } from 'helpers/store/models/AppState';
import { useTranslationWithDataKey } from 'hooks/useTranslationWithDataKey';
import { ClearContextRequestActionInputs } from 'models/context/ClearContext';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { ExternalUser } from 'models/context/ExternalUser';
import { InternalUser } from 'models/context/InternalUser';
import { LegacyCrmDeeplinkCommand, LegacyCrmDeeplinkConfig } from 'models/LegacyCrmDeeplink';
import { AuthUserType, logoutActions, selectAuthUserType } from 'saga/auth/ducks';
import {
  getCustomerListActions,
  selectContextCustomerInfo,
  selectCustomerList,
  setContextCustomerActions,
} from 'saga/context/customer/ducks';
import { clearContextAction } from 'saga/context/ducks';
import { selectContextExternalUser } from 'saga/context/external/ducks';
import {
  selectContextInternalIsUserScreenFilterDisabled,
  selectContextInternalUser,
} from 'saga/context/internal/ducks';
import { redirectToLegacyCrmAction } from 'saga/crm-deeplinks/ducks';
import HasSimpleUserPrivilege from 'views/components/ACL/HasSimpleUserPrivilege';
import { isUnidentifiedResidentCustomer } from 'views/layout/NewLayout/utils';
import { environment } from 'environments/environment';

interface Props {
  id: string;
  popoverElm: PopoverState;
  handlePopoverClose: () => void;
  incompleteCustomer: boolean;
}

const UserPopper = ({
  id,
  popoverElm,
  handlePopoverClose,
  contextCustomer,
  contextExternalUser,
  contextInternalUser,
  customerList,
  getCustomerList,
  logout,
  setContextCustomer,
  clearContext,
  isInternal,
  authUserType,
  redirectToLegacyCrm,
  incompleteCustomer,
}: StateToProps & Props & DispatchToProps) => {
  const { t } = useTranslationWithDataKey();
  const history = useHistory();
  const missingName = environment.properties['gui.general.BFFDIGITVCAPTURE.contactPerson.missingName'];

  useEffect(() => {
    if (contextExternalUser) {
      const cpRefNo = contextExternalUser && contextInternalUser && contextExternalUser.cpRefNo;

      getCustomerList(cpRefNo);
    }
  }, [getCustomerList, contextExternalUser, contextInternalUser]);

  const displayCustomerList = useMemo(
    () => customerList.filter((customer) => customer.cuRefNo !== contextCustomer?.cuRefNo),
    [contextCustomer, customerList]
  );

  const showAllInvoices = useMemo(
    () => customerList.filter((customer) => !isUnidentifiedResidentCustomer(customer)).length > 1,
    [customerList]
  );

  const handleSelectCustomer = (cuRefNo: string) => () => {
    setContextCustomer({ cuRefNo });
    linkToExternalDashboard(cuRefNo);
  };

  const handleClearContext = () => {
    handlePopoverClose();

    const clearContextPayload: ClearContextRequestActionInputs = {};

    if (authUserType === 'internal') {
      clearContextPayload.deeplinkAfterClearContext = {
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { page: 'internalDashboard' },
      };
    }

    clearContext(clearContextPayload);
  };

  const handleLogout = () => {
    logout();
  };

  const linkToExternalDashboard = (cuRefNo?: string) => {
    const queryParams = { cuRefNo: cuRefNo || contextCustomer?.cuRefNo };

    if (authUserType === 'internal') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: queryParams,
      });
    } else if (authUserType === 'external') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.EMO2DASH,
        queryParams: queryParams,
      });
    }
  };

  const linkToSettings = () => {
    if (authUserType === 'internal') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { page: 'customerSettings' },
      });
    } else if (authUserType === 'external') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.EMO2DASH,
        queryParams: { page: 'customerSettings' },
      });
    }
  };

  const linkToCustomerNote = () => {
    if (authUserType === 'internal') {
      redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { page: 'customerNote' },
      });
    }
  };

  const getJournalingHistory = () => {
    history.push(
      buildRoute(ROUTES.EventHistoryList, {
        entityTypeId: 'CUSTOMER',
        entityRefNo: contextCustomer?.cuRefNo || '',
      })
    );
    handlePopoverClose();
  };

  const handleAllInvoicesClick: MouseEventHandler = () => {
    history.push(buildRoute(ROUTES.AllInvoices));
  };
  const getActiveCustomerName = () => {
    if (isUnidentifiedResidentCustomer(contextCustomer)) {
      if (!contextExternalUser) {
        return {
          value: ' ',
          label: t('onecrm.gui_header.externalHeader.unidentifiedCustomer', false),
        };
      }

      if (contextExternalUser?.lastName === missingName) {
        return {
          label: t('onecrm.gui_header.externalHeader.customer', false),
          value: contextExternalUser?.login,
        };
      }
    }

    return {
      label: t('onecrm.gui_header.externalHeader.customer', false),
      value: contextCustomer?.displayName,
    };
  };

  const getCustomerListName = (customer: CustomerInfo, externalUser: ExternalUser | null) => {
    if (isUnidentifiedResidentCustomer(customer)) {
      if (!externalUser) {
        return t('onecrm.gui_header.externalHeader.unidentifiedCustomer');
      }

      if (externalUser?.lastName === missingName) {
        return externalUser?.login;
      }
    }

    return customer.displayName;
  };

  return (
    <O2UserPopper
      id={id}
      open={popoverElm.id === id}
      targetElm={popoverElm.target as Element}
      onClose={handlePopoverClose}
      customer={{
        id: contextExternalUser?.login,
        name: contextCustomer && getActiveCustomerName().value, //data-elm="11.1.3_011"
        type: !!contextCustomer?.segment && contextCustomer.segment !== CustomerSegment.R ? 'business' : 'resident',
      }}
      customerIdLabel={t('onecrm.gui_header.externalHeader.access.via')}
      customerNameLabel={contextCustomer && getActiveCustomerName().label} //data-elm="11.1.3_011"
      onCustomerIdClick={() => linkToExternalDashboard(contextCustomer?.cuRefNo)}
      internal={isInternal}
      logoutBtnText={
        isInternal
          ? t('onecrm.gui_externalHeader.action.cpClearContextButton')
          : t('onecrm.gui_externalHeader.action.logout')
      }
      onLogoutBtnClick={isInternal && contextInternalUser ? handleClearContext : handleLogout}
      footerLinks={
        displayCustomerList.length ? (
          <>
            {displayCustomerList.map((customer) => (
              <O2UserPopper.Link
                onClick={handleSelectCustomer(customer.cuRefNo)}
                key={customer.cuRefNo}
                data-elm="11.1.3_015"
                icon={!!customer.segment && customer.segment !== CustomerSegment.R ? 'business' : 'resident'}
                label={getCustomerListName(customer, contextExternalUser)}
              />
            ))}
          </>
        ) : undefined
      }
    >
      <Grid container spacing={1.5}>
        {contextCustomer && (
          <HasSimpleUserPrivilege privilegeType="ORDER_ADMIN">
            <Grid item xs={12} md={6}>
              <Button
                onClick={linkToCustomerNote}
                size="small"
                data-elm="11.1.3_013"
                variant="outlined"
                fullWidth
                color="internal"
              >
                {t('onecrm.gui_externalHeader.action.addNoteButton')}
              </Button>
            </Grid>
          </HasSimpleUserPrivilege>
        )}
        {isInternal && contextCustomer && (
          <HasSimpleUserPrivilege privilegeType="JOURNALING_VIEW">
            <Grid item xs={12} md={6}>
              <Button
                onClick={getJournalingHistory}
                size="small"
                data-elm="11.1.3_008"
                variant="outlined"
                fullWidth
                color="internal"
              >
                {t('onecrm.gui_externalHeader.action.historyButton')}
              </Button>
            </Grid>
          </HasSimpleUserPrivilege>
        )}
        {contextCustomer && !incompleteCustomer && (
          <Grid item xs={12} md={6}>
            <Button
              onClick={() => linkToExternalDashboard(contextCustomer.cuRefNo)}
              size="small"
              data-elm="11.1.3_018"
              variant="outlined"
              fullWidth
              startIcon={<Home />}
            >
              {t('onecrm.gui_externalHeader.action.myServices')}
            </Button>
          </Grid>
        )}
        {contextCustomer && !incompleteCustomer && (
          <Grid item xs={12} md={6}>
            <Button
              onClick={linkToSettings}
              size="small"
              data-elm="11.1.3_012"
              variant="outlined"
              fullWidth
              startIcon={<Settings />}
            >
              {t('onecrm.gui_externalHeader.action.cuCpSettings')}
            </Button>
          </Grid>
        )}
        {showAllInvoices && (
          <Grid item xs={12}>
            <Button
              size="small"
              data-elm="11.1.3_024"
              variant="outlined"
              fullWidth
              startIcon={<Calendar />}
              onClick={handleAllInvoicesClick}
            >
              {t('onecrm.gui_externalHeader.allInvoices')}
            </Button>
          </Grid>
        )}
      </Grid>
    </O2UserPopper>
  );
};

interface StateToProps {
  contextCustomer: CustomerInfo | null;
  contextExternalUser: ExternalUser | null;
  contextInternalUser: InternalUser | null;
  customerList: CustomerInfo[];
  isInternal: boolean;
  authUserType: AuthUserType;
}

interface DispatchToProps {
  logout: () => void;
  getCustomerList: (cpRefNo?: string | null) => void;
  setContextCustomer: ({ cuRefNo }: any) => void;
  clearContext: (payload: ClearContextRequestActionInputs) => void;
  redirectToLegacyCrm: (deeplinkConfig: LegacyCrmDeeplinkConfig) => void;
}

const mapStateToProps = (state: AppState): StateToProps => ({
  contextCustomer: selectContextCustomerInfo(state),
  contextExternalUser: selectContextExternalUser(state),
  contextInternalUser: selectContextInternalUser(state),
  customerList: selectCustomerList(state),
  isInternal: selectContextInternalIsUserScreenFilterDisabled(state),
  authUserType: selectAuthUserType(state),
});

const mapDispatchToProps: DispatchToProps = {
  logout: logoutActions.request,
  getCustomerList: getCustomerListActions.request,
  setContextCustomer: setContextCustomerActions.request,
  clearContext: clearContextAction.request,
  redirectToLegacyCrm: redirectToLegacyCrmAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserPopper);
